import React, { useEffect, useState } from 'react';
import AppTooltip from '../../components/Tooltip/Tooltip';

interface ApiStructureDropDownProps {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => void;
    required?: boolean;
    baseOptions?: string[];
}

const DEFAULT_API_STRUCTURES = [
    "REST (Representational State Transfer) API",
    "SOAP (Simple Object Access Protocol) API",
    "GraphQL API",
    "gRPC (Google Remote Procedure Call) API",
    "WebSockets API",
    "Webhook (Event Callback) API",
    "RPC (Remote Procedure Call) API",
    "HATEOAS (Hypermedia as the Engine of Application State) API",
    "Streaming APIs",
    "Motorola Standard Native API",
];

const ApiStructureDropDown: React.FC<ApiStructureDropDownProps> = ({ value, onChange, required = false, baseOptions }) => {
    const [isCustom, setIsCustom] = useState(value === "Other...");
    const [customValue, setCustomValue] = useState("");
    const [options, setOptions] = useState(() => {
        const base = baseOptions && baseOptions.length > 0 ? baseOptions : DEFAULT_API_STRUCTURES;
        return [...base, "Other..."];
    });

    useEffect(() => {
        if (baseOptions && baseOptions.length > 0) {
            setOptions((prevOptions) => {
                const withCustom = [...baseOptions, "Other..."];
                // Preserve only user-typed custom values (not defaults or fallbacks)
                prevOptions.forEach((opt) => {
                    if (!withCustom.includes(opt) && !DEFAULT_API_STRUCTURES.includes(opt)) {
                        withCustom.splice(withCustom.length - 1, 0, opt);
                    }
                });
                return withCustom;
            });
        }
    }, [baseOptions]);

    useEffect(() => {
        if (value && !options.includes(value)) {
            setOptions((prevOptions) => [...prevOptions, value]);
        }
    }, [value, options]);

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedValue = e.target.value;
        setIsCustom(selectedValue === "Other...");
        onChange(e);
    };

    const handleCustomInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCustomValue(e.target.value);
    };

    const handleAddCustomOption = () => {
        if (customValue.trim() && !options.includes(customValue)) {
            const newOptions = [...options, customValue];
            setOptions(newOptions);
            setIsCustom(false); // Hide the input and button
            setCustomValue(""); // Clear the custom input field
            onChange({ target: { name: "apiStructure", value: customValue } } as React.ChangeEvent<HTMLSelectElement>);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleAddCustomOption();
        }
    };

    return (
        <div>
            <label htmlFor="apiStructure" className="form-label">
                API Structure <span className="text-danger">*</span>
            </label>
            <div className="position-relative input-group">
                <select
                    id="apiStructure"
                    name="apiStructure"
                    className="form-select"
                    value={value} // Use the managed selected value
                    onChange={handleSelectChange}
                    required={required}
                >
                    <option value="">Select</option>
                    {options.map((option, index) => (
                        <option key={index} value={option}>
                            {option}
                        </option>
                    ))}
                </select>
                <AppTooltip
                    title='The architectural style or pattern the API follows'
                    placement="bottom"
                />   
            </div>
            {isCustom && (
                <div className="d-flex align-items-center mt-2">
                    <input
                        type="text"
                        id="customApiStructure"
                        name="customApiStructure"
                        className="form-control me-2"
                        placeholder="Enter custom API structure"
                        value={customValue}
                        onChange={handleCustomInputChange}
                        onKeyPress={handleKeyPress}
                        required={required}
                    />
                    <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={handleAddCustomOption}
                    >
                        Confirm
                    </button>
                </div>
            )}
        </div>
    );
};

export default ApiStructureDropDown;